import { Button, Checkbox, Field, Input, Modal } from "@love-cpmd/ui";
import { ArrowLeftRight, ArrowRight, BadgeCheck, CalendarCheck2, CircleAlert, Eye, EyeOff, IdCard, KeyRound, LifeBuoy, LockKeyhole, NotebookText, X } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import officeBackdrop from "../assets/cpmd-office-blur.webp";
import { FullPageLoader } from "../components/common/FullPageLoader";
import { LogoMark } from "../components/common/layout-bits";
import { API_MODE, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useDocumentTitle } from "../lib/hooks";
import { useMembers } from "../lib/queries";

function safeNext(next: string | null): string {
  // Only allow in-app paths.
  if (!next || !next.startsWith("/") || next.startsWith("//") || next.startsWith("/login")) return "/dashboard";
  return next;
}

export function LoginPage() {
  useDocumentTitle("Log in");
  const { userId, ready, login } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const next = safeNext(params.get("next"));

  const [memberId, setMemberId] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  if (!ready) return <FullPageLoader />;
  if (userId) return <Navigate to={next} replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!memberId || !password) {
      setError("Enter your Member ID and password.");
      return;
    }
    setPending(true);
    setError(null);
    try {
      await login(memberId, password, remember);
      navigate(next, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "We couldn't log you in. Please try again.");
      setPending(false);
    }
  };

  return (
    <div className="relative isolate min-h-dvh overflow-hidden bg-[#2a0a16]">
      {/* The CPMD office, blurred at build time (cheaper than a live CSS blur on phones). A burgundy wash
          keeps the left column readable and lets the red ceiling lights glow through on the right. */}
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10">
        <img src={officeBackdrop} alt="" decoding="async" fetchPriority="high" className="size-full scale-105 object-cover object-[64%_28%] lg:object-[62%_center]" />
        {/* Phone: lighter at the top so the red ceiling lights show above the card. Desktop: dark on the left for the headline. */}
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(40,6,20,0.62)_0%,rgba(46,8,23,0.40)_28%,rgba(36,6,18,0.62)_62%,rgba(24,5,13,0.85)_100%)] lg:hidden" />
        <div className="absolute inset-0 hidden bg-[linear-gradient(100deg,rgba(38,6,19,0.92)_0%,rgba(62,10,31,0.74)_38%,rgba(44,8,24,0.42)_72%,rgba(24,6,14,0.34)_100%)] lg:block" />
        <div className="absolute inset-0 hidden bg-[radial-gradient(120%_70%_at_50%_115%,rgba(18,4,10,0.7),transparent_62%)] lg:block" />
      </div>

      <div className="relative mx-auto flex min-h-dvh w-full max-w-[1180px] flex-col px-4 md:px-8">
        <header className="flex items-center justify-between pt-6 pb-4 md:pt-8">
          <div className="flex items-center gap-2.5">
            <LogoMark className="size-11 drop-shadow-[0_4px_14px_rgba(0,0,0,0.35)]" decorative />
            <div className="flex flex-col">
              <span className="text-[15px] leading-none font-extrabold tracking-tight text-white">LOVE CPMD</span>
              <span className="mt-1 text-label-sm text-white/70">Knowledge Sharing</span>
            </div>
          </div>
          <span className="hidden items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-label-sm text-white/80 ring-1 ring-white/15 backdrop-blur-md sm:flex">
            <LockKeyhole className="size-4 text-primary-fixed" aria-hidden /> Internal CPMD access
          </span>
        </header>

        <main className="grid grid-cols-1 flex-1 items-center gap-10 py-8 lg:grid-cols-12">
          {/* Editorial column */}
          <section className="hidden flex-col gap-8 lg:col-span-6 lg:flex xl:col-span-5">
            <span className="inline-flex w-fit items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-label-sm font-bold tracking-wider text-white uppercase ring-1 ring-white/20 backdrop-blur-md">
              <span className="size-1.5 rounded-full bg-primary-fixed motion-safe:animate-pulse" /> CPMD Weekly Sharing
            </span>
            <div className="flex flex-col gap-3">
              <h1 className="text-headline-xl text-balance text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.25)]">Every week, one of us teaches the rest.</h1>
              <p className="max-w-md text-body-lg text-white/75">
                The schedule, the rotation, and every summary of the team's weekly knowledge sharing — in one place, on your laptop or phone.
              </p>
            </div>
            <ul className="flex flex-col gap-3">
              {[
                { icon: CalendarCheck2, title: "Know who's next", text: "This week's presenter and your own turn, right on the dashboard." },
                { icon: ArrowLeftRight, title: "Swap in a few taps", text: "Reschedule or swap dates without breaking the rotation." },
                { icon: NotebookText, title: "Catch up on what you missed", text: "Summaries and key takeaways from every session." },
              ].map((f) => (
                <li key={f.title} className="flex items-start gap-3.5 rounded-2xl bg-white/[0.07] p-3.5 ring-1 ring-white/15 backdrop-blur-md">
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary-container text-on-primary shadow-brand ring-1 ring-white/10">
                    <f.icon className="size-5" aria-hidden />
                  </span>
                  <span>
                    <span className="block text-label-lg text-white">{f.title}</span>
                    <span className="block text-body-md text-white/70">{f.text}</span>
                  </span>
                </li>
              ))}
            </ul>
          </section>

          {/* Login card */}
          <section className="flex justify-center lg:col-span-6 xl:col-span-7">
            <div className="relative w-full max-w-[460px] overflow-hidden rounded-[22px] border border-white/50 bg-surface-container-lowest p-6 shadow-[0_28px_80px_-24px_rgba(16,3,9,0.7)] sm:p-8">
              <div aria-hidden className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-primary via-secondary to-primary-container" />
              <div className="flex flex-col items-center pb-6 text-center">
                <LogoMark className="mb-3 size-20 drop-shadow-[0_8px_18px_rgba(150,20,50,0.22)]" />
                <h2 className="text-headline-lg text-on-surface">Log in</h2>
                <p className="mt-1 max-w-xs text-body-md text-on-surface-variant">Use your CPMD Member ID to open the schedule and summaries.</p>
              </div>

              <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
                <Field label="Member ID" htmlFor="memberId" aside="Format: CPMDxxx">
                  <Input
                    id="memberId"
                    name="username"
                    autoComplete="username"
                    autoCapitalize="characters"
                    spellCheck={false}
                    placeholder="CPMD001"
                    leading={<IdCard />}
                    value={memberId}
                    maxLength={10}
                    onChange={(e) => setMemberId(e.target.value.toUpperCase().replace(/\s/g, ""))}
                    className="font-semibold tracking-wider"
                    autoFocus
                  />
                </Field>
                <Field
                  label="Password"
                  htmlFor="password"
                  aside={
                    <button type="button" onClick={() => setHelpOpen(true)} className="font-semibold text-primary hover:underline">
                      Need help?
                    </button>
                  }
                >
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    leading={<KeyRound />}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    trailing={
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        className="inline-flex size-9 items-center justify-center rounded-lg text-on-surface-variant hover:bg-surface-container hover:text-on-surface"
                      >
                        {showPassword ? <EyeOff className="size-[18px]" /> : <Eye className="size-[18px]" />}
                      </button>
                    }
                  />
                </Field>
                <Checkbox label="Keep me signed in on this device" description="Up to 30 days" checked={remember} onChange={(e) => setRemember(e.target.checked)} />

                {error && (
                  <div role="alert" className="flex items-start gap-2.5 rounded-xl bg-error-container px-3.5 py-3 text-on-error-container">
                    <CircleAlert className="mt-0.5 size-[18px] shrink-0 text-error" aria-hidden />
                    <span className="flex-1 text-body-md font-medium">{error}</span>
                    <button type="button" aria-label="Dismiss" onClick={() => setError(null)} className="rounded p-0.5 hover:text-error">
                      <X className="size-4" />
                    </button>
                  </div>
                )}

                <Button type="submit" size="lg" block loading={pending} trailing={<ArrowRight />} className="mt-1">
                  {pending ? "Checking your details…" : "Log in"}
                </Button>
              </form>

              <p className="mt-5 text-center text-body-sm text-on-surface-variant">
                Forgot password?{" "}
                <button type="button" onClick={() => setHelpOpen(true)} className="font-semibold text-primary hover:underline">
                  Contact your System Manager
                </button>
                .
              </p>
            </div>
          </section>
        </main>

        <footer className="flex flex-col items-center justify-between gap-2 border-t border-white/15 py-5 text-label-sm text-white/65 sm:flex-row">
          <span className="flex items-center gap-2">
            <BadgeCheck className="size-4 text-primary-fixed" aria-hidden /> LOVE CPMD · v1.0 MVP
          </span>
          <span>Weekly knowledge sharing for the CPMD team</span>
        </footer>
      </div>

      <HelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
}

function HelpDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  // Online, the member list needs a login, so the dialog shows the general advice only.
  const { data: members = [] } = useMembers(API_MODE === "mock" && open);
  const managers = members.filter((m) => m.role === "system_manager" && m.active);
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Account help"
      icon={<LifeBuoy />}
      size="sm"
      footer={
        <Button variant="tonal" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-body-md text-on-surface-variant">
          For a password reset, a new Member ID, or login problems, contact your System Manager. They can reset your password from Manage System.
        </p>
        {managers.map((m) => (
          <div key={m.id} className="flex items-center justify-between rounded-xl bg-surface-container-low px-4 py-3">
            <div>
              <p className="text-label-lg text-on-surface">{m.name}</p>
              <p className="text-body-sm text-on-surface-variant">System Manager · {m.memberId}</p>
            </div>
            {m.email && (
              <a href={`mailto:${m.email}`} className="text-label-md text-primary hover:underline">
                Email
              </a>
            )}
          </div>
        ))}
      </div>
    </Modal>
  );
}
